import { useState } from "react";
import { api } from "@/lib/api";
import { X, Briefcase, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { money } from "@/lib/money";

// Admin: flag a worker as a Sales Rep with $/hr commission + weekly draw, and send their portal login.
export default function SalesRepModal({ worker: w, onClose, onChanged }) {
  const [on, setOn] = useState(!!w.is_sales_rep);
  const [rate, setRate] = useState(w.sales_comp?.rate_per_hour ?? 10);
  const [draw, setDraw] = useState(w.sales_comp?.weekly_draw ?? 2000);
  const [since, setSince] = useState(w.sales_rep_since || new Date().toISOString().slice(0, 10));
  const [review, setReview] = useState(w.sales_review_required !== false);
  const [busy, setBusy] = useState(false);
  const r = parseFloat(rate) || 0, d = parseFloat(draw) || 0;
  const breakEven = r > 0 ? d / r : 0;

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.put(`/workers/${w.id}/sales-rep`, { is_sales_rep: on, rate_per_hour: r, weekly_draw: d, since: on && since ? since : null, review_required: review });
      const switched = data?.login_switched === "sales" ? " Their existing login now opens the sales portal (not the timesheet page)."
                     : data?.login_switched === "contractor" ? " Their login goes back to the contractor timesheet portal." : "";
      toast.success((on ? `${w.name} is set up as a sales rep — break-even ${breakEven.toFixed(0)} hrs/week.` : `${w.name} is no longer flagged as a sales rep.`) + switched, { duration: switched ? 9000 : 5000 });
      onChanged(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || err.message); }
    finally { setBusy(false); }
  };
  const sendLogin = async () => {
    if (!window.confirm(`Email ${w.name} their sales portal login${w.email ? ` at ${w.email}` : ""}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/workers/${w.id}/sales-rep/send-login`);
      toast.success(`Login ${data.login_created ? "created and " : ""}emailed to ${data.email}.`);
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50" data-testid="sales-rep-modal" onClick={onClose}>
      <form onSubmit={save} className="bg-white rounded-md w-full max-w-md p-6 border border-slate-200 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xl font-bold flex items-center gap-2"><Briefcase size={22} weight="bold" className="text-emerald-600" /> Sales rep</h2>
          <button type="button" onClick={onClose} data-testid="sales-rep-close" className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="text-sm text-slate-500 mb-4">{w.name}</div>

        <label className="flex items-start gap-3 text-sm cursor-pointer p-3 rounded-md border border-slate-200 bg-slate-50">
          <input type="checkbox" checked={on} onChange={(e) => setOn(e.target.checked)} data-testid="sales-rep-toggle" className="mt-0.5 h-4 w-4 accent-emerald-600" />
          <span><b>This worker is a sales rep.</b> <span className="text-slate-600">Projects can be credited to them; approved hours on those projects drive their pay. Their login (existing or sent below) opens the <b>sales portal</b> instead of the contractor timesheet page.</span></span>
        </label>

        {on && (
          <>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Rate per approved hour</label>
                <div className="relative mt-1"><span className="absolute left-3 top-2.5 text-slate-400">$</span>
                  <input type="number" step="0.01" min="0" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="sales-rep-rate"
                         className="w-full h-10 pl-7 pr-3 border border-slate-300 rounded-md font-mono" /></div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Weekly draw (guarantee)</label>
                <div className="relative mt-1"><span className="absolute left-3 top-2.5 text-slate-400">$</span>
                  <input type="number" step="0.01" min="0" value={draw} onChange={(e) => setDraw(e.target.value)} data-testid="sales-rep-draw"
                         className="w-full h-10 pl-7 pr-3 border border-slate-300 rounded-md font-mono" /></div>
              </div>
            </div>
            <div className="mt-3">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Plan starts week of</label>
              <input type="date" value={since} onChange={(e) => setSince(e.target.value)} data-testid="sales-rep-since"
                     className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-md" />
              <div className="text-xs text-slate-400 mt-1">Snaps to that Monday. Weeks before it earn no draw and don't count toward YTD — set it back if the rep has history you want paid.</div>
            </div>
            <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="sales-rep-break-even">
              Weekly pay = the greater of <b>{money(d)}</b> or <b>hours × {money(r)}</b>.
              {r > 0 && <> Break-even: <b>{breakEven.toFixed(breakEven % 1 ? 1 : 0)} approved hrs/week</b>; above that, commission replaces the draw.</>}
            </div>
            <label className={`mt-3 flex items-start gap-3 text-sm cursor-pointer p-3 rounded-md border ${review ? "border-amber-300 bg-amber-50" : "border-teal-300 bg-teal-50"}`} data-testid="sales-rep-review-box">
              <input type="checkbox" checked={review} onChange={(e) => setReview(e.target.checked)} data-testid="sales-rep-review-toggle" className="mt-0.5 h-4 w-4 accent-emerald-600" />
              <span>
                <b>Require my approval before their proposals and assignment packets go out.</b>
                <span className="block text-slate-600 mt-0.5">
                  {review
                    ? "Drafts wait in your review queue; you approve & send or send them back with a note."
                    : "Off — the rep sends directly to clients and workers. You'll get an FYI email each time, and can still edit or void afterward."}
                </span>
              </span>
            </label>
            <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-200 pt-4">
              <div className="text-xs text-slate-500">
                Portal login{w.sales_login_sent_at ? <> — last sent {String(w.sales_login_sent_at).slice(0, 10)}</> : " — not sent yet"}
                {!w.email && <span className="block text-rose-600 font-semibold">Add an email to this worker first.</span>}
              </div>
              <button type="button" onClick={sendLogin} disabled={busy || !w.is_sales_rep || !w.email} data-testid="sales-rep-send-login"
                      title={!w.is_sales_rep ? "Save first, then send the login" : ""}
                      className="h-10 px-3 rounded-md border border-slate-900 bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-40">
                <PaperPlaneTilt size={16} weight="bold" /> {w.sales_login_sent_at ? "Resend login" : "Send login"}
              </button>
            </div>
          </>
        )}

        <div className="mt-5 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="h-11 px-4 border border-slate-300 rounded-md font-semibold">Cancel</button>
          <button disabled={busy} data-testid="sales-rep-save" className="h-11 px-5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-md disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}
